<template>
  <el-dialog @close="onClosed" title="修改磅单号" :visible.sync="visible" width="760px">
    <el-form :model="form" :rules="rules" ref="ruleForm" label-width="100px">
      <el-form-item label="磅单号" prop="pound_code">
        <el-input
          style="width: 300px;"
          v-model="form.pound_code"
          @blur="previewGuofeiWeightNote"
          @input="debouncePreview"
        ></el-input>
      </el-form-item>
    </el-form>
    <div v-if="isGuofei" class="sap-preview">
      <el-alert
        v-if="previewError"
        :title="previewError"
        class="mb-2"
        show-icon
        type="error"
        :closable="false"
      />
      <el-alert
        v-else
        class="mb-2"
        show-icon
        type="info"
        :closable="false"
        title="国废业务确认后会重新查询 SAP，并同步更新包数、抽包号、订单和物料字段。"
      />
      <el-table
        v-if="sapDiffs.length > 0"
        v-loading="previewLoading"
        :data="sapDiffs"
        border
        size="mini"
      >
        <el-table-column label="字段" prop="field_name" width="130" />
        <el-table-column label="SAP字段" min-width="110">
          <template slot-scope="{ row }">
            <span>{{ row.sap_field || "--" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="当前值" min-width="170">
          <template slot-scope="{ row }">
            <span>{{ formatDiffValue(row, row.current_value) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="SAP值" min-width="170">
          <template slot-scope="{ row }">
            <span :class="{ 'text-warning': row.changed }">{{ formatDiffValue(row, row.sap_value) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div v-else-if="previewLoading" class="text-gray-500 preview-placeholder">
        正在获取 SAP 磅单信息...
      </div>
      <div v-else class="text-gray-500 preview-placeholder">
        输入磅单号后自动获取 SAP 磅单信息。
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="close">取 消</el-button>
      <el-button size="small" type="primary" :loading="submitLoading" @click="ok">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  data() {
    return {
      visible: false,
      isGuofei: false,
      previewLoading: false,
      submitLoading: false,
      previewError: "",
      previewTimer: null,
      previewResult: null,
      rules: {
        pound_code: [
          { required: true, message: "请输入磅单号", trigger: "change" }
        ]
      },
      form: {
        booking_id: "",
        pound_code: ""
      }
    };
  },
  computed: {
    sapDiffs() {
      return (this.previewResult && this.previewResult.diffs) || [];
    }
  },
  methods: {
    // 打开修改磅单号弹窗，国废业务会启用 SAP 磅单信息预览。
    open(pound_code, booking_id, isGuofei = false) {
      this.resetState();
      this.isGuofei = isGuofei;
      this.$set(this.form, "booking_id", booking_id);
      this.$set(this.form, "pound_code", pound_code);
      this.visible = true;
    },
    // 关闭弹窗并触发关闭清理。
    close() {
      this.visible = false;
    },
    // 弹窗关闭后重置输入、预览和校验状态。
    onClosed() {
      this.resetState();
      this.$nextTick(() => {
        this.$refs.ruleForm.clearValidate();
      })
    },
    // 重置弹窗内部状态，避免上一次预览结果影响下一条预约记录。
    resetState() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
      }
      this.previewTimer = null;
      this.previewLoading = false;
      this.submitLoading = false;
      this.previewError = "";
      this.previewResult = null;
      this.isGuofei = false;
      this.form = {
        booking_id: "",
        pound_code: ""
      };
    },
    // 输入磅单号时防抖查询 SAP，避免每个字符都触发接口请求。
    debouncePreview() {
      if (!this.isGuofei) {
        return;
      }
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
      }
      this.previewTimer = setTimeout(() => {
        this.previewGuofeiWeightNote();
      }, 500);
    },
    // 统一格式化 SAP 差异展示值，大小包直接展示为大包/小包。
    formatDiffValue(row, value) {
      if (row.field_key === "is_small_package") {
        return value === "X" ? "小包" : "大包";
      }
      return value || "--";
    },
    // 根据当前磅单号预览 SAP 磅单信息差异。
    async previewGuofeiWeightNote() {
      if (!this.isGuofei || !this.form.booking_id || !this.form.pound_code) {
        this.previewResult = null;
        this.previewError = "";
        return;
      }
      if (this.previewTimer) {
        clearTimeout(this.previewTimer);
        this.previewTimer = null;
      }
      this.previewLoading = true;
      this.previewError = "";
      try {
        const res = await this.$common.http({
          url: "/api/queue/admin/preview_guofei_weight_note",
          data: {
            booking_id: this.form.booking_id,
            pound_code: this.form.pound_code
          }
        });
        this.previewResult = res.data.data || {};
      } catch (error) {
        // 关键步骤：预览失败不关闭弹窗，保留用户输入，便于修正磅单号后重新查询。
        this.previewResult = null;
        this.previewError =
          (error && error.message) ||
          (error && error.errmsg) ||
          "获取 SAP 磅单信息失败";
      } finally {
        this.previewLoading = false;
      }
    },
    // 确认修改磅单号；国废业务由后端重新查询 SAP 后同步相关字段。
    ok() {
      this.$refs.ruleForm.validate(async v => {
        if (v) {
          try {
            this.submitLoading = true;
            await this.$common.http({
              url: "/api/queue/admin/update_booking_pound",
              data: this.form
            });
            this.$emit('ok');
            this.close();
          } catch (error) {
            console.log(error);
          } finally {
            this.submitLoading = false;
          }
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.sap-preview {
  margin-top: 8px;
}

.mb-2 {
  margin-bottom: 8px;
}

.preview-placeholder {
  padding-left: 100px;
  line-height: 28px;
}

.text-warning {
  color: #e6a23c;
  font-weight: 600;
}
</style>
